import { useContext, useState, ChangeEvent, useEffect } from "react";
import UiContext from "@contexts/UiContext";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwButton from "@components/shared/OwButton/OwButton";
import { SocketContext } from "@contexts/SocketContext";
import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import { OwAccordion } from "@components/shared/OwAccordion/OwAccordion";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import { v4 as uuidv4 } from "uuid";
import IItem_Option from "@models/IItem_Option";
import INewItem_Option from "@models/INewItem_Option";
import { OwFnInput } from "@components/shared/OwFnInput/OwFnInput";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";
import OwCheckbox from "@components/shared/OwCheckbox/OwCheckbox";
import EndpointsEnum from "@models/EndpointsEnum";
import ICreateItemOptionDTO from "@models/DTO/ICreateItemOptionDTO";
import IUpdateItemOptionDTO from "@models/DTO/IUpdateItemOptionDTO";
import IOnCreateItemOptionDTO from "@models/DTO/IOnCreateItemOptionDTO";
import IOnUpdateItemOptionDTO from "@models/DTO/IOnUpdateItemOptionDTO";
import IUiSocketSetup from "@models/IUiSocketSetup";
import IOnGetCatalogItemOptionsDTO from "@models/DTO/IOnGetCatalogItemOptionsDTO";
import IGetCatalogItemOptionsDTO from "@models/DTO/IGetCatalogItemOptionsDTO";
import TitleTagEnum from "@models/TitleTagEnum";
import { useIntl } from "react-intl";
type Props = {
    catalogId: number;
    catalogGuid: string;
};

// Define our own interface that doesn't extend IItem_Option to avoid type issues
interface IItemOptionWithUuid {
    uuid: string;
    isNew?: boolean;
    catalog_id: number;
    name: string;
    description?: string;
    type: "dropdown" | "radio" | "checkbox" | "text" | "number";
    options?: { value: string; title: string }[];
    required: boolean;
    display_order?: number;
    id: number;
    guid: string;
    created_at: string;
    updated_at: string;
}

const OPTION_TYPES = [
    { id: "dropdown", name: "Dropdown" },
    { id: "radio", name: "Radio" },
    { id: "checkbox", name: "Checkbox" },
    { id: "text", name: "Text" },
    { id: "number", name: "Number" },
];

export const ItemOptionsSidebar = (props: Props) => {
    const uiContext = useContext(UiContext);
    const socket = useContext(SocketContext)!;
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const intl = useIntl();

    const [itemOptions, setItemOptions] = useState<IItemOptionWithUuid[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const onGetCatalogItemOptions = (data: IOnGetCatalogItemOptionsDTO) => {
        const itemOptionsWithUuid = data.itemOptions.map((item) => ({
            ...item,
            uuid: uuidv4(),
            isNew: false,
            catalog_id: props.catalogId,
            id: item.id,
            guid: item.guid,
            created_at: item.created_at,
            updated_at: item.updated_at,
        }));

        setItemOptions(itemOptionsWithUuid);
    };

    const getCatalogItemOptions = () => {
        const data: IGetCatalogItemOptionsDTO = {
            catalogGuid: props.catalogGuid,
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.CatalogItemOptionGetCatalogItemOptions, data);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.CatalogItemOptionOnGetCatalogItemOptions, fn: onGetCatalogItemOptions }];

    useEffect(() => {
        sockets.forEach((item) => {
            socket.on(item.endpoint, item.fn);
        });

        getCatalogItemOptions();

        return () => {
            sockets.forEach((item) => {
                socket.off(item.endpoint, item.fn);
            });
        };
    }, []);

    // Add a new empty item option
    const addNewItemOption = () => {
        const newItemOption: IItemOptionWithUuid = {
            uuid: uuidv4(),
            catalog_id: props.catalogId,
            name: "",
            description: "",
            type: "dropdown",
            options: [],
            required: false,
            display_order: itemOptions.length,
            isNew: true,
            id: 0,
            guid: "",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };

        setItemOptions([...itemOptions, newItemOption]);
    };

    // Remove an item option
    const removeItemOption = (uuid: string) => {
        const updatedOptions = itemOptions.filter((option) => option.uuid !== uuid);
        setItemOptions(updatedOptions);
    };

    // Update an item option
    const updateItemOption = (uuid: string, field: string, value: any) => {
        const updatedOptions = itemOptions.map((option) => {
            if (option.uuid === uuid) {
                return {
                    ...option,
                    [field]: value,
                };
            }
            return option;
        });
        setItemOptions(updatedOptions);
    };

    // Add a new option value to an item option
    const addOptionValue = (uuid: string) => {
        const updatedOptions = itemOptions.map((option) => {
            if (option.uuid === uuid) {
                const currentOptions = option.options || [];
                return {
                    ...option,
                    options: [...currentOptions, { value: "", title: "" }],
                };
            }
            return option;
        });
        setItemOptions(updatedOptions);
    };

    // Remove an option value from an item option
    const removeOptionValue = (uuid: string, index: number) => {
        const updatedOptions = itemOptions.map((option) => {
            if (option.uuid === uuid && option.options) {
                const newOptions = [...option.options];
                newOptions.splice(index, 1);
                return {
                    ...option,
                    options: newOptions,
                };
            }
            return option;
        });
        setItemOptions(updatedOptions);
    };

    // Update an option value
    const updateOptionValue = (uuid: string, index: number, field: string, value: string) => {
        const updatedOptions = itemOptions.map((option) => {
            if (option.uuid === uuid && option.options) {
                const newOptions = [...option.options];
                newOptions[index] = {
                    ...newOptions[index],
                    [field]: value,
                };
                return {
                    ...option,
                    options: newOptions,
                };
            }
            return option;
        });
        setItemOptions(updatedOptions);
    };

    // Save all item options
    const saveItemOptions = () => {
        if (!socket) return;

        setIsLoading(true);

        const createPromises = itemOptions
            .filter((option) => option.isNew)
            .map((option) => {
                const { isNew, uuid, ...itemOption } = option;
                const createData: ICreateItemOptionDTO = {
                    catalogGuid: props.catalogGuid,
                    itemOption: {
                        name: itemOption.name,
                        description: itemOption.description,
                        type: itemOption.type as "dropdown" | "radio" | "checkbox" | "text" | "number",
                        options: itemOption.options,
                        required: itemOption.required,
                        display_order: itemOption.display_order,
                    },
                    userGuid: userModel.guid,
                    userToken: userModel.token,
                    supplierGuid: supplierModel.guid,
                };

                return new Promise((resolve) => {
                    socket.emit(EndpointsEnum.CatalogItemOptionCreateItemOption, createData);
                    socket.once(EndpointsEnum.CatalogItemOptionOnCreateItemOption, (data: IOnCreateItemOptionDTO) => {
                        resolve(data);
                    });
                });
            });

        const updatePromises = itemOptions
            .filter((option) => !option.isNew && option.id !== 0)
            .map((option) => {
                const { uuid, ...itemOption } = option;
                const updateData: IUpdateItemOptionDTO = {
                    itemOption: itemOption as any, // Cast to any to avoid type errors
                    userGuid: userModel.guid,
                    userToken: userModel.token,
                    supplierGuid: supplierModel.guid,
                };

                return new Promise((resolve) => {
                    socket.emit(EndpointsEnum.CatalogItemOptionUpdateItemOption, updateData);
                    socket.once(EndpointsEnum.CatalogItemOptionOnUpdateItemOption, (data: IOnUpdateItemOptionDTO) => {
                        resolve(data);
                    });
                });
            });

        console.log("Promises", createPromises, updatePromises);

        Promise.all([...createPromises, ...updatePromises])
            .then(() => {
                setIsLoading(false);
                uiContext.toggleSidebar();
            })
            .catch((error) => {
                console.error("Error saving item options:", error);
                setIsLoading(false);
            });
    };

    // Handle input change events
    const handleInputChange = (e: ChangeEvent<HTMLInputElement>, uuid: string, field: string) => {
        updateItemOption(uuid, field, e.target.value);
    };

    // Handle select change events
    const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>, uuid: string) => {
        updateItemOption(uuid, "type", e.target.value);
    };

    // Handle checkbox change events
    const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>, uuid: string) => {
        updateItemOption(uuid, "required", e.target.checked);
    };

    return (
        <div className={"ItemOptionsSidebar p-2"}>
            <div className="d-flex align-items-center justify-content-between">
                <OwTitle leftIconName={"fad fa-boxes"} title={"item_options"} tag={"h3"} />
                <OwButton centerIconClass={"fas fa-times"} fn={() => uiContext.toggleSidebar()} className="btn-danger" />
            </div>

            <div className="my-3">
                <OwButton leftIconClass="fas fa-plus" title="add_item_option" fn={addNewItemOption} className="btn-primary btn-sm" />
            </div>

            <div className="item-options-container">
                {itemOptions.map((itemOption) => (
                    <OwAccordion
                        key={itemOption.uuid}
                        title={itemOption.name || intl.formatMessage({ id: "new_item_option" })}
                        className="mb-3"
                        fullWidthClick={true}
                        initOpen={itemOption.isNew}
                        disableTranslation={true}
                        rightElem={
                            <div className="ms-auto">
                                <OwButton
                                    centerIconClass="fas fa-trash-alt"
                                    fn={() => removeItemOption(itemOption.uuid)}
                                    className="btn-danger btn-sm me-2"
                                />
                            </div>
                        }
                        titleTag={TitleTagEnum.H5}>
                        <div className="p-2">
                            {/* <div className="d-flex justify-content-end mt-3">
                                <OwButton
                                    centerIconClass="fas fa-trash"
                                    fn={() => removeItemOption(itemOption.uuid)}
                                    className="btn-danger btn-sm me-2"
                                />
                            </div> */}
                            <div className="mb-3">
                                <OwLabel title="name" showIsReqAsterik={true} />
                                <OwFnInput
                                    name="name"
                                    value={itemOption.name}
                                    fn={(e: ChangeEvent<HTMLInputElement>) => handleInputChange(e, itemOption.uuid, "name")}
                                />
                            </div>

                            <div className="mb-3">
                                <OwLabel title="description" />
                                <OwFnInput
                                    name="description"
                                    value={itemOption.description || ""}
                                    fn={(e: ChangeEvent<HTMLInputElement>) => handleInputChange(e, itemOption.uuid, "description")}
                                />
                            </div>

                            <div className="mb-3">
                                <OwLabel title="type" showIsReqAsterik={true} />
                                <OwSelectOption
                                    name="type"
                                    valueProp="id"
                                    titleProp="name"
                                    options={OPTION_TYPES}
                                    fn={(e: ChangeEvent<HTMLSelectElement>) => handleSelectChange(e, itemOption.uuid)}
                                    selectedOption={itemOption}
                                    selectedOptionProp="type"
                                />
                            </div>

                            <div className="mb-3">
                                <OwCheckbox
                                    name="required"
                                    label="required"
                                    checked={itemOption.required}
                                    fn={(e: ChangeEvent<HTMLInputElement>) => handleCheckboxChange(e, itemOption.uuid)}
                                />
                            </div>

                            {(itemOption.type === "dropdown" || itemOption.type === "radio") && (
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <OwLabel title="options" />
                                        <OwButton
                                            centerIconClass="fas fa-plus"
                                            fn={() => addOptionValue(itemOption.uuid)}
                                            className="btn-secondary btn-sm"
                                        />
                                    </div>

                                    {(itemOption.options || []).map((option, index) => (
                                        <div key={index} className="d-flex mb-2 align-items-center">
                                            <div className="flex-grow-1 me-2">
                                                <OwFnInput
                                                    name={`value-${index}`}
                                                    placeholder="value"
                                                    value={option.value}
                                                    fn={(e: ChangeEvent<HTMLInputElement>) =>
                                                        updateOptionValue(itemOption.uuid, index, "value", e.target.value)
                                                    }
                                                />
                                            </div>
                                            <div className="flex-grow-1 me-2">
                                                <OwFnInput
                                                    name={`title-${index}`}
                                                    placeholder="title"
                                                    value={option.title}
                                                    fn={(e: ChangeEvent<HTMLInputElement>) =>
                                                        updateOptionValue(itemOption.uuid, index, "title", e.target.value)
                                                    }
                                                />
                                            </div>
                                            <OwButton
                                                centerIconClass="fas fa-times"
                                                fn={() => removeOptionValue(itemOption.uuid, index)}
                                                className="btn-danger btn-sm"
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </OwAccordion>
                ))}
            </div>

            <div className="d-flex justify-content-end mt-4">
                <OwButton title="cancel" className="btn-secondary me-2" fn={() => uiContext.toggleSidebar()} />
                <OwButton title="save" className="btn-primary" fn={saveItemOptions} disabled={isLoading} active={isLoading} />
            </div>
        </div>
    );
};
